import { AsyncPipe, NgFor, NgIf, TitleCasePipe } from '@angular/common';
import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  InjectionToken,
  input,
  OnInit,
  output,
  ViewChild
} from '@angular/core';
import { FlexModule } from '@angular/flex-layout';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatTooltip } from '@angular/material/tooltip';
import {
  injectMutation,
  injectQuery
} from '@tanstack/angular-query-experimental';
import { MomentModule } from 'ngx-moment';
import { lastValueFrom, Observable, of, timer } from 'rxjs';
import { filter, mergeMap, take, takeUntil } from 'rxjs/operators';
import { Customer, Delivery } from '../../../models/index';
import { CompleteDeliveryDialogComponent } from '../../dialogs/complete-delivery-dialog/complete-delivery-dialog.component';
import {
  DeliveryDialogComponent,
  DeliveryDialogData
} from '../../dialogs/delivery-dialog/delivery-dialog.component';
import { base64ToBlob } from '../../helpers/base64ToBlob';
import { ConfirmDialogComponent } from '../../index';
import { HlCurrencyPipe } from '../../pipes/hl-currency.pipe';
import { NewLinePipe } from '../../pipes/new-line.pipe';
import { ConfigService } from '../../services/config.service';
import { DeliveryService } from '../../services/delivery.service';
import { DeliveryStatusComponent } from '../delivery-status.component';
import { DeliverableItemListPipe } from './deliverable-item-list.pipe';
import {
  DeliverableItem,
  DeliverableService,
  DeliveryListItem
} from './deliverable-service.interface';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CustomerAddressPipe } from '../../pipes/customer-address.pipe';

// Create injection token for deliverable service
export const DELIVERABLE_SERVICE = new InjectionToken<any>(
  'DELIVERABLE_SERVICE'
);

@Component({
  selector: 'deliveries',
  templateUrl: './docket-delivery-list.component.html',
  styleUrls: ['./docket-delivery-list.component.scss'],
  providers: [DeliveryService, CustomerAddressPipe],
  standalone: true,
  imports: [
    FlexModule,
    NgIf,
    NgFor,
    MatIconButton,
    MatTooltip,
    MatIcon,
    DeliveryStatusComponent,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    MatButton,
    TitleCasePipe,
    NewLinePipe,
    MomentModule,
    DeliverableItemListPipe,
    HlCurrencyPipe,
    AsyncPipe
  ]
})
export class DocketDeliveryListComponent implements OnInit {
  deliverableId = input.required<number>();
  deliverableType = input.required<'docket' | 'booking'>();
  deliverableCustomer = input.required<Customer>();
  refreshDeliveryData$ = input.required<Observable<void>>();
  defaultCurrency = input('GBP');
  deliverableCurrency = input('GBP');
  currencyRate = input(1);
  canAdd = input(true);
  canEdit = input(true);
  customerAddressPipe = inject(CustomerAddressPipe);
  deliverableCustomerAddress = computed(() =>
    this.customerAddressPipe.transform(this.deliverableCustomer())
  );
  // Convert outputs to signals
  deliveriesUpdated = output<DeliveryListItem[]>();

  // State signals
  dateTimeFormat = ConfigService.dates.shortDateTimeFormat;
  @ViewChild('printFrame') printFrame;

  // Inject services
  private dialog = inject(MatDialog);
  private deliveryService = inject(DeliveryService);
  private deliverableService = inject(
    DELIVERABLE_SERVICE
  ) as DeliverableService;

  // Create mutation for loading deliveries
  deliveriesForDeliverable = injectQuery(() => ({
    queryKey: [this.deliverableType(), this.deliverableId(), 'deliveries'],
    queryFn: () =>
      lastValueFrom(this.deliverableService.getDeliveries(this.deliverableId()))
  }));

  deliveries = computed(
    () => this.deliveriesForDeliverable.data()?.deliveries ?? []
  );
  potentialDeliveryItems = computed(
    () => this.deliveriesForDeliverable.data()?.deliverable_items ?? []
  );

  // Create mutation for completing delivery
  completeDeliveryMutation = injectMutation(() => ({
    mutationFn: (delivery: Delivery) =>
      lastValueFrom(this.deliveryService.complete(delivery.id, delivery)),
    onSuccess: (delivery) => {
      this.deliveriesForDeliverable.refetch();
    }
  }));

  private destroyRef = inject(DestroyRef);

  constructor() {
    effect(() => this.deliveriesUpdated.emit(this.deliveries()));
  }

  ngOnInit() {
    this.refreshDeliveryData$()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.deliveriesForDeliverable.refetch();
      });
  }

  addDelivery(prefillData?: DeliveryDialogData['prefillData']) {
    const hasPreviousDelivery =
      this.deliveries().filter((delivery) => delivery.type === 'delivery')
        .length > 0;
    this.dialog
      .open(DeliveryDialogComponent, {
        data: {
          deliverableType: this.deliverableType(),
          deliverableId: this.deliverableId(),
          currency: this.deliverableCurrency(),
          currencyRate: this.currencyRate(),
          customer: this.deliverableCustomer(),
          items: this.potentialDeliveryItems().map((item) => ({
            ...item,
            on_job: false
          })),
          type: hasPreviousDelivery ? 'collection' : 'delivery',
          customerId: this.deliverableCustomer().id,
          prefillData
        } as DeliveryDialogData
      })
      .afterClosed()
      .pipe(filter((value) => !!value))
      .subscribe(() => {
        this.deliveriesForDeliverable.refetch();
      });
  }

  addCollectionFromPreviousDelivery(delivery: DeliveryListItem) {
    this.addDelivery({
      type: 'collection',
      address: delivery.location,
      charge: delivery.charge,
      notes: delivery.notes,
      location: delivery.location,
      google_place_id: delivery.google_place_id,
      items: delivery.deliverableItemIds,
      allocated_to: delivery.allocated_to
    });
  }

  print(delivery: DeliveryListItem, download = false) {
    this.deliveryService.print(delivery.id).subscribe((response) => {
      let src = URL.createObjectURL(base64ToBlob(response, 'application/pdf'));
      if (download) {
        const link = document.createElement('a') as HTMLAnchorElement;
        link.href = src;
        const ref =
          (delivery.type === 'delivery' ? 'Delivery' : 'Collection') +
          delivery.id;
        link.download = `${ref}.pdf`;
        link.click();
      } else {
        this.printFrame.nativeElement.src = src;
        this.printIframe();
      }
    });
  }

  printIframe() {
    timer(500).subscribe(() => {
      this.printFrame.nativeElement.contentWindow.print();
    });
  }

  download(delivery: DeliveryListItem) {
    this.print(delivery, true);
  }

  showEditDialog(delivery: DeliveryListItem) {
    const deliveryDisplayItems = this.getDeliveryDisplayItems(delivery);
    this.dialog
      .open(DeliveryDialogComponent, {
        data: {
          deliverableId: this.deliverableId(),
          deliverableType: this.deliverableType(),
          items: deliveryDisplayItems,
          customer: this.deliverableCustomer(),
          currency: this.deliverableCurrency(),
          currencyRate: this.currencyRate(),
          type: delivery.type,
          delivery: delivery,
          customerId: this.deliverableCustomer().id
        } as DeliveryDialogData
      })
      .afterClosed()
      .pipe(filter((updated) => !!updated))
      .subscribe(() => {
        this.deliveriesForDeliverable.refetch();
      });
  }

  showCancelDialog(delivery: DeliveryListItem) {
    this.dialog
      .open(ConfirmDialogComponent, {
        data: {
          title: `Cancel ${delivery.type}`,
          message: `Are you sure you want to cancel this ${delivery.type}?`,
          type: 'cancel'
        }
      })
      .afterClosed()
      .pipe(
        take(1),
        mergeMap((confirmed) => {
          if (confirmed) {
            return this.deliveryService.delete(delivery.id);
          }
          return of(null);
        })
      )
      .subscribe(() => {
        this.deliveriesForDeliverable.refetch();
      });
  }

  showCompleteDialog(delivery: DeliveryListItem) {
    const itemsOnDeliveryAssigned = delivery.deliverableItemIds.filter(
      (itemId) =>
        this.potentialDeliveryItems().find(
          (item) => item.deliverable_item_id === itemId
        )?.assigned
    );
    if (itemsOnDeliveryAssigned.length !== delivery.deliverableItemIds.length) {
      this.dialog.open(ConfirmDialogComponent, {
        data: {
          title: 'Items not assigned',
          message:
            'The items on this delivery have not been assigned yet, please edit the booking items above to assign specific items before completing this delivery.',
          type: 'ok'
        }
      });
      return;
    }

    this.dialog
      .open(CompleteDeliveryDialogComponent, {
        maxWidth: '40em',
        data: {
          delivery: {
            ...delivery,
            deliverable: {
              id: this.deliverableId(),
              type: this.deliverableType(),
              customer_address: this.deliverableCustomerAddress()
            }
          }
        }
      })
      .afterClosed()
      .pipe(
        take(1),
        mergeMap((result) => {
          if (result) {
            return this.deliveryService.get(delivery.id, [
              'items.deliverable_item'
            ]);
          }
          return of(null);
        }),
        filter((result) => !!result)
      )
      .subscribe(() => {
        this.deliveriesForDeliverable.refetch();
      });
  }

  private getDeliveryDisplayItems(
    delivery: DeliveryListItem
  ): DeliverableItem[] {
    return this.potentialDeliveryItems().map((item) => ({
      ...item,
      on_job: delivery.deliverableItemIds.includes(item.deliverable_item_id)
    }));
  }

  openInGoogleMaps(location: string, placeId: string | null) {
    const address = location.replace('\n', ' ');
    if (placeId) {
      window.open(
        `https://www.google.com/maps/search/?api=1&query=${address}&query_place_id=${placeId}`,
        '_blank'
      );
      return;
    }
    window.open(
      `https://www.google.com/maps/search/?api=1&query=${address}`,
      '_blank'
    );
  }
}
